import { useEffect, useState } from "react";

/**
 * Internal microcopy of the design-system components (labels that never reach the apps' translation files).
 * The active language is read from `<html lang>`, which the i18n package keeps in sync, so the UI package
 * stays free of an i18n dependency while still rendering Arabic and English.
 */
const EN = {
  close: "Close",
  you: "You",
  showDraft: "Show draft & citations",
  hideDraft: "Hide draft",
  noResults: "No results",
  previousPage: "Previous page",
  nextPage: "Next page",
  switchLanguage: "Switch language",
  mainMenu: "Main menu",
  riskHigh: "High risk",
  riskElevated: "Elevated risk",
  riskLow: "Low risk",
  breadcrumb: "You are here",
  toggleNavigation: "Toggle navigation",
  collapseSidebar: "Collapse sidebar",
  expandSidebar: "Expand sidebar",
  quality: "Quality",
  confidence: "Confidence",
  editorBold: "Bold",
  editorItalic: "Italic",
  editorStrike: "Strikethrough",
  editorHeading: "Heading",
  editorSubheading: "Subheading",
  editorBulletList: "Bulleted list",
  editorOrderedList: "Numbered list",
  editorTaskList: "Checklist",
  editorQuote: "Quote",
  editorLink: "Link",
  editorUnlink: "Remove link",
  editorLinkPrompt: "Link address",
  editorTable: "Insert table",
  editorUndo: "Undo",
  daysLeft: "days left",
  daysOver: "days over",
  paused: "paused",
  editorRedo: "Redo",
  editorPlaceholder: "Write here…",
  editorFormatting: "Formatting",
  confirm: "Confirm",
  cancel: "Cancel",
  irreversible: "This cannot be undone.",
  search: "Search",
  searchPlaceholder: "Search policies, ministries, records and reference codes",
  searchEmpty: "Nothing matches. Try a policy code, a ministry or part of a title.",
  searchHint: "Type at least two letters",
  reportIssue: "Report an issue",
  reportIssueTitle: "Report an issue on this screen",
  reportIssueHint: "Describe what you expected and what happened. A picture of the screen and the page address are attached automatically.",
  reportIssueCategory: "Type",
  reportIssueWording: "Wording",
  reportIssueLayout: "Layout",
  reportIssueError: "Error",
  reportIssueSuggestion: "Suggestion",
  reportIssueOther: "Other",
  reportIssueDescription: "What happened",
  reportIssueScreenshot: "Attach a picture of the screen",
  reportIssueSend: "Send report",
  reportIssueSent: "Thank you. The report has reached the platform team.",
  reportIssueFailed: "The report could not be sent. Try again.",
  sessionTitle: "Your session is about to end",
  sessionBody: "For your security you will be signed out in {time}. Unsaved text is kept on this device and restored when you sign in again.",
  sessionExtend: "Stay signed in",
  sessionSignOut: "Sign out now",
  sessionExtended: "Your session has been extended.",
  sessionEnded: "Your session has ended. Sign in again to continue; unsaved text will be restored.",
  whatsNew: "What's new",
  whatsNewTitle: "What's new in version {version}",
  changed: "Changed",
  added: "Added",
  removed: "Removed",
  version: "Version",
  draftRestored: "Your unsaved text was restored.",
  draftDiscard: "Discard",
  working: "Working…",
  citationOpen: "Click to open the source",
  citationUnverified: "The citation check could not confirm this excerpt supports the claim.",
  citationNoExcerpt: "No excerpt was captured for this source.",
  citationPage: "page {page}",
  sourceRegulation: "Legislation",
  sourceDocument: "Document",
  sourceDataBank: "Statistics",
  sourcePrecedent: "Precedent",
  sourceTool: "Tool result",
  sourceWeb: "Web source",
  classificationPublic: "Public",
  classificationInternal: "Internal",
  classificationConfidential: "Confidential",
  classificationSecret: "Secret",
  classificationLabel: "Information classification",
  filter: "Filter",
  filterColumn: "Filter {column}",
  columns: "Columns",
  toggleColumns: "Show or hide columns",
  selectAll: "Select all rows",
  selectRow: "Select row",
  selectedRows: "{count} selected",
  clearSelection: "Clear selection",
  rowsPerPage: "Rows per page",
  pageOf: "Page {page} of {pages}",
  rangeOf: "{from}–{to} of {total}",
  sortAscending: "Sorted ascending",
  sortDescending: "Sorted descending",
  notSorted: "Not sorted",
  dropzoneTitle: "Drag files here or choose from your device",
  dropzoneHint: "Up to {size} per file",
  dropzoneBrowse: "Choose files",
  dropzoneRejected: "{name} was not accepted",
  removeFile: "Remove {name}",
  kanbanEmpty: "Nothing in this column",
  kanbanMoveHint: "Press space to pick up a card, arrow keys to move it and space again to drop it.",
  diffInserted: "Inserted",
  diffDeleted: "Deleted",
  editorHighlight: "Highlight",
  loading: "Loading…",
  tryAgain: "Try again",
  stepCompleted: "completed",
  stepCurrent: "current step",
  stepUpcoming: "upcoming",
  target: "Target",
  paletteNavigate: "to move",
  paletteOpen: "to open",
  paletteDismiss: "to close",
  askAi: "Ask AI",
  assistant: "Assistant",
  closeAssistant: "Close the assistant",
  resizePanel: "Resize the panel",
  machineTranslated: "Machine translated",
  showOriginal: "Show the original",
  showTranslation: "Show the translation",
  translationPending: "Translation in progress",
  translationFailed: "Translation unavailable",
  textLanguage: "Language of this text",
  originalIn: "Original in {language}",
  languageEnglish: "English",
  languageArabic: "Arabic",
  fileTooLarge: "{name} is larger than {size}",
  fileTooSmall: "{name} is empty",
  fileInvalidType: "{name} is not a file type this form accepts",
  tooManyFiles: "Too many files at once. Choose fewer files and try again.",
  more: "More",
  previousMonth: "Previous month",
  nextMonth: "Next month",
  pickDate: "Pick a date",
  pickDateRange: "Pick a date range",
  clear: "Clear",
  today: "Today",
  expand: "Expand",
  collapse: "Collapse",
  selectPlaceholder: "Choose…",
  viewTable: "Table",
  viewCards: "Cards",
  viewBoard: "Board",
  viewAs: "View as",
  saved: "Saved",
  saving: "Saving…",
  unsaved: "Unsaved changes",
  stepOf: "Step {current} of {total}",
  back: "Back",
  next: "Next",
  finish: "Finish",
  settings: "Settings",
  onThisPage: "On this page",
  aiGenerated: "Generated by AI",
  dismiss: "Dismiss",
  platformNavigation: "Platform navigation",
  pageActions: "Page actions",
  apply: "Apply",
  applied: "Applied",
  undo: "Undo",
  send: "Send",
  attach: "Attach files",
  suggestedPrompts: "Suggested questions",
  toastRegion: "Notifications",
  closeToast: "Close notification",
  brandName: "Policy AI",
  uaeEmblem: "Emblem of the United Arab Emirates",
};

const AR: Record<keyof typeof EN, string> = {
  close: "إغلاق",
  you: "أنت",
  showDraft: "عرض المسودة والاستشهادات",
  hideDraft: "إخفاء المسودة",
  noResults: "لا توجد نتائج",
  previousPage: "الصفحة السابقة",
  nextPage: "الصفحة التالية",
  switchLanguage: "تبديل اللغة",
  mainMenu: "القائمة الرئيسية",
  riskHigh: "مخاطر مرتفعة",
  riskElevated: "مخاطر متوسطة",
  riskLow: "مخاطر منخفضة",
  breadcrumb: "موقعك في المنصة",
  toggleNavigation: "إظهار قائمة التنقل أو إخفاؤها",
  collapseSidebar: "طي الشريط الجانبي",
  expandSidebar: "توسيع الشريط الجانبي",
  quality: "الجودة",
  confidence: "درجة الثقة",
  editorBold: "غامق",
  editorItalic: "مائل",
  editorStrike: "يتوسطه خط",
  editorHeading: "عنوان",
  editorSubheading: "عنوان فرعي",
  editorBulletList: "قائمة نقطية",
  editorOrderedList: "قائمة مرقمة",
  editorTaskList: "قائمة تحقق",
  editorQuote: "اقتباس",
  editorLink: "رابط",
  editorUnlink: "إزالة الرابط",
  editorLinkPrompt: "عنوان الرابط",
  editorTable: "إدراج جدول",
  editorUndo: "تراجع",
  daysLeft: "يوم متبقٍ",
  daysOver: "يوم تأخير",
  paused: "متوقف مؤقتاً",
  editorRedo: "إعادة",
  editorPlaceholder: "اكتب النص هنا…",
  editorFormatting: "التنسيق",
  confirm: "تأكيد",
  cancel: "إلغاء",
  irreversible: "لا يمكن التراجع عن هذا الإجراء.",
  search: "بحث",
  searchPlaceholder: "البحث في السياسات والجهات والسجلات والرموز المرجعية",
  searchEmpty: "لا توجد نتائج مطابقة. يمكنك البحث برمز السياسة أو اسم الجهة أو جزء من العنوان.",
  searchHint: "اكتب حرفين على الأقل",
  reportIssue: "الإبلاغ عن مشكلة",
  reportIssueTitle: "الإبلاغ عن مشكلة في هذه الشاشة",
  reportIssueHint: "صِف ما كنت تتوقعه وما حدث فعلاً. تُرفق صورة الشاشة وعنوان الصفحة تلقائياً.",
  reportIssueCategory: "النوع",
  reportIssueWording: "الصياغة",
  reportIssueLayout: "التصميم",
  reportIssueError: "خطأ",
  reportIssueSuggestion: "اقتراح",
  reportIssueOther: "أخرى",
  reportIssueDescription: "ما الذي حدث",
  reportIssueScreenshot: "إرفاق صورة الشاشة",
  reportIssueSend: "إرسال البلاغ",
  reportIssueSent: "شكراً لك. وصل البلاغ إلى فريق المنصة.",
  reportIssueFailed: "تعذّر إرسال البلاغ. يُرجى المحاولة مرة أخرى.",
  sessionTitle: "توشك الجلسة على الانتهاء",
  sessionBody: "حفاظاً على أمان حسابك، سيُسجَّل خروجك خلال {time}. ويُحتفظ بالنص غير المحفوظ على هذا الجهاز، ويُستعاد عند تسجيل الدخول مجدداً.",
  sessionExtend: "متابعة الجلسة",
  sessionSignOut: "تسجيل الخروج الآن",
  sessionExtended: "مُدِّدت الجلسة.",
  sessionEnded: "انتهت الجلسة. يُرجى تسجيل الدخول مجدداً للمتابعة، وسيُستعاد النص غير المحفوظ.",
  whatsNew: "ما الجديد",
  whatsNewTitle: "ما الجديد في الإصدار {version}",
  changed: "ما تغيّر",
  added: "ما أُضيف",
  removed: "ما أُزيل",
  version: "الإصدار",
  draftRestored: "استُعيد النص غير المحفوظ.",
  draftDiscard: "تجاهل",
  working: "جارٍ العمل…",
  citationOpen: "انقر لفتح المصدر",
  citationUnverified: "تعذّر على فحص الاستشهادات التأكد من أن هذا المقتطف يدعم ما ورد في النص.",
  citationNoExcerpt: "لم يُحفظ أي مقتطف من هذا المصدر.",
  citationPage: "الصفحة {page}",
  sourceRegulation: "تشريع",
  sourceDocument: "مستند",
  sourceDataBank: "إحصاءات",
  sourcePrecedent: "سابقة",
  sourceTool: "نتيجة أداة",
  sourceWeb: "مصدر من الإنترنت",
  classificationPublic: "عام",
  classificationInternal: "داخلي",
  classificationConfidential: "سري",
  classificationSecret: "سري للغاية",
  classificationLabel: "تصنيف المعلومات",
  filter: "تصفية",
  filterColumn: "تصفية {column}",
  columns: "الأعمدة",
  toggleColumns: "إظهار الأعمدة أو إخفاؤها",
  selectAll: "تحديد جميع الصفوف",
  selectRow: "تحديد الصف",
  selectedRows: "المحدَّد: {count}",
  clearSelection: "إلغاء التحديد",
  rowsPerPage: "عدد الصفوف في الصفحة",
  pageOf: "الصفحة {page} من {pages}",
  rangeOf: "{from}–{to} من {total}",
  sortAscending: "مرتّب تصاعدياً",
  sortDescending: "مرتّب تنازلياً",
  notSorted: "غير مرتّب",
  dropzoneTitle: "اسحب الملفات إلى هنا أو اخترها من جهازك",
  dropzoneHint: "الحد الأقصى {size} لكل ملف",
  dropzoneBrowse: "اختيار الملفات",
  dropzoneRejected: "لم يُقبل الملف {name}",
  removeFile: "إزالة {name}",
  kanbanEmpty: "لا توجد عناصر في هذا العمود",
  kanbanMoveHint: "اضغط مفتاح المسافة لالتقاط البطاقة، ثم مفاتيح الأسهم لنقلها، ثم مفتاح المسافة مجدداً لإفلاتها.",
  diffInserted: "مضاف",
  diffDeleted: "محذوف",
  editorHighlight: "تمييز",
  loading: "جارٍ التحميل…",
  tryAgain: "إعادة المحاولة",
  stepCompleted: "مكتملة",
  stepCurrent: "المرحلة الحالية",
  stepUpcoming: "قادمة",
  target: "المستهدف",
  paletteNavigate: "للتنقل",
  paletteOpen: "للفتح",
  paletteDismiss: "للإغلاق",
  askAi: "سؤال المساعد الذكي",
  assistant: "المساعد الذكي",
  closeAssistant: "إغلاق المساعد الذكي",
  resizePanel: "تغيير حجم اللوحة",
  machineTranslated: "ترجمة آلية",
  showOriginal: "عرض النص الأصلي",
  showTranslation: "عرض الترجمة",
  translationPending: "الترجمة قيد الإعداد",
  translationFailed: "الترجمة غير متاحة",
  textLanguage: "لغة هذا النص",
  originalIn: "النص الأصلي باللغة {language}",
  languageEnglish: "الإنجليزية",
  languageArabic: "العربية",
  fileTooLarge: "حجم الملف {name} أكبر من {size}",
  fileTooSmall: "الملف {name} فارغ",
  fileInvalidType: "نوع الملف {name} غير مقبول في هذا النموذج",
  tooManyFiles: "عدد الملفات يتجاوز الحد المسموح به في المرة الواحدة. يُرجى اختيار عدد أقل والمحاولة مرة أخرى.",
  more: "المزيد",
  previousMonth: "الشهر السابق",
  nextMonth: "الشهر التالي",
  pickDate: "اختر تاريخاً",
  pickDateRange: "اختر فترة زمنية",
  clear: "مسح",
  today: "اليوم",
  expand: "توسيع",
  collapse: "طي",
  selectPlaceholder: "اختر…",
  viewTable: "جدول",
  viewCards: "بطاقات",
  viewBoard: "لوحة",
  viewAs: "طريقة العرض",
  saved: "حُفظت التغييرات",
  saving: "جارٍ الحفظ…",
  unsaved: "تغييرات غير محفوظة",
  stepOf: "الخطوة {current} من {total}",
  back: "رجوع",
  next: "التالي",
  finish: "إنهاء",
  settings: "الإعدادات",
  onThisPage: "في هذه الصفحة",
  aiGenerated: "مُنشأ بالذكاء الاصطناعي",
  dismiss: "إخفاء",
  platformNavigation: "التنقل في المنصة",
  pageActions: "إجراءات الصفحة",
  apply: "تطبيق",
  applied: "مُطبَّق",
  undo: "تراجع",
  send: "إرسال",
  attach: "إرفاق ملفات",
  suggestedPrompts: "أسئلة مقترحة",
  toastRegion: "الإشعارات",
  closeToast: "إغلاق الإشعار",
  brandName: "منصة السياسات الوطنية الذكية",
  uaeEmblem: "شعار دولة الإمارات العربية المتحدة",
};

const MICROCOPY = { en: EN, ar: AR } as const;

export type MicrocopyKey = keyof typeof EN;
export type UiLocale = keyof typeof MICROCOPY;

/** BCP 47 tags for `Intl` formatting: Western digits and the Gregorian calendar in Arabic, as the rest of the platform uses. */
export const INTL_TAG: Record<UiLocale, string> = { en: "en-AE", ar: "ar-AE-u-nu-latn-ca-gregory" };

/** CLDR's Arabic puts the tanween fath before the alif (U+064B U+0627); UAE government Arabic writes it on the alif (يوماً). Apply to Arabic Intl output. */
export function normalizeArabicIntl(text: string): string {
  return text.replace(/\u064B\u0651\u0627|\u0651\u064B\u0627/g, "\u0651\u0627\u064B").replace(/\u064B\u0627/g, "\u0627\u064B");
}

export function documentLocale(): UiLocale {
  if (typeof document === "undefined") return "en";
  return document.documentElement.lang.toLowerCase().startsWith("ar") ? "ar" : "en";
}

/** Reacts to language switches by observing the `lang` attribute the i18n layer writes on `<html>`. */
export function useDocumentLocale(): UiLocale {
  const [locale, setLocale] = useState<UiLocale>(documentLocale);
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(() => setLocale(documentLocale()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
    return () => observer.disconnect();
  }, []);
  return locale;
}

export function microcopy(key: MicrocopyKey, locale: UiLocale = documentLocale(), values?: Record<string, string | number>): string {
  const text: string = MICROCOPY[locale][key] ?? MICROCOPY.en[key];
  return values ? text.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? String(values[k]) : m)) : text;
}

export function useMicrocopy(): (key: MicrocopyKey, values?: Record<string, string | number>) => string {
  const locale = useDocumentLocale();
  return (key, values) => microcopy(key, locale, values);
}
